import { useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Input } from '@actual-app/components/input';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';

import { FinancialText } from '#components/FinancialText';
import type { ReadyFinancialDashboard } from '#components/product-finance/client';
import { createCommandId } from '#components/product-finance/command-id';
import { getFinancialErrorCode } from '#components/product-finance/errors';
import {
  formatMoneyInput,
  formatProductDate,
  formatProductMoney,
  getDeviceLocalDate,
  parseMoneyInput,
} from '#components/product-finance/money';

import { EmptySection, SectionCard } from './dashboard-shared';

type ProgressInput = Readonly<{
  goalId: string;
  kind: 'contribution' | 'withdrawal';
  amountMinorUnits: number;
  date: string;
  note?: string;
  commandId?: string;
}>;

export function GoalsCard({
  dashboard,
  onSetContribution,
  onRecordProgress,
  onDeleteProgressEntry,
  onUseEmergencyFund,
}: {
  dashboard: ReadyFinancialDashboard;
  onSetContribution: (
    goalId: string,
    amountMinorUnits: number,
  ) => Promise<void>;
  onRecordProgress: (input: ProgressInput) => Promise<void>;
  onDeleteProgressEntry: (goalId: string, entryId: string) => Promise<void>;
  onUseEmergencyFund: (goalId: string, availableMinorUnits: number) => void;
}) {
  return (
    <SectionCard title={<Trans>Goals</Trans>}>
      {dashboard.financialGoals.length === 0 ? (
        <EmptySection>
          <Trans>No financial goals yet.</Trans>
        </EmptySection>
      ) : (
        dashboard.financialGoals.map(summary => (
          <GoalProgressRow
            key={`${summary.goal.id}:${summary.progressAmount.minorUnits}:${
              dashboard.goalContributions.find(
                item => item.goalId === summary.goal.id,
              )?.amount.minorUnits ?? 0
            }`}
            summary={summary}
            cycleContribution={dashboard.goalContributions.find(
              item => item.goalId === summary.goal.id,
            )}
            isCurrentCycle={dashboard.currentCycle !== null}
            onSetContribution={onSetContribution}
            onRecordProgress={onRecordProgress}
            onDeleteProgressEntry={onDeleteProgressEntry}
            onUseEmergencyFund={onUseEmergencyFund}
          />
        ))
      )}
    </SectionCard>
  );
}

function GoalProgressRow({
  summary,
  cycleContribution,
  isCurrentCycle,
  onSetContribution,
  onRecordProgress,
  onDeleteProgressEntry,
  onUseEmergencyFund,
}: {
  summary: ReadyFinancialDashboard['financialGoals'][number];
  cycleContribution:
    | ReadyFinancialDashboard['goalContributions'][number]
    | undefined;
  isCurrentCycle: boolean;
  onSetContribution: (
    goalId: string,
    amountMinorUnits: number,
  ) => Promise<void>;
  onRecordProgress: (input: ProgressInput) => Promise<void>;
  onDeleteProgressEntry: (goalId: string, entryId: string) => Promise<void>;
  onUseEmergencyFund: (goalId: string, availableMinorUnits: number) => void;
}) {
  const { t } = useTranslation();
  const [kind, setKind] = useState<ProgressInput['kind']>('contribution');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(getDeviceLocalDate());
  const [note, setNote] = useState('');
  const [cycleAmount, setCycleAmount] = useState(
    cycleContribution === undefined
      ? ''
      : formatMoneyInput(cycleContribution.amount),
  );
  const [isSaving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const [hasUnknownOutcome, setHasUnknownOutcome] = useState(false);
  // A retry of the same entry reuses its command id so a reply lost after the
  // write cannot record the contribution twice.
  const lastAttempt = useRef<{ signature: string; commandId: string } | null>(
    null,
  );
  function commandIdFor(payload: unknown): string {
    const signature = JSON.stringify(payload);
    if (lastAttempt.current?.signature !== signature) {
      lastAttempt.current = { signature, commandId: createCommandId() };
    }
    return lastAttempt.current.commandId;
  }

  async function saveProgress() {
    const parsedAmount = parseMoneyInput(amount);
    const availableMinorUnits =
      kind === 'contribution'
        ? summary.remainingAmount.minorUnits
        : summary.progressAmount.minorUnits;
    if (
      parsedAmount === null ||
      parsedAmount <= 0 ||
      parsedAmount > availableMinorUnits ||
      date.length === 0
    ) {
      setError(true);
      return;
    }
    setSaving(true);
    setError(false);
    try {
      const payload = {
        goalId: summary.goal.id,
        kind,
        amountMinorUnits: parsedAmount,
        date,
        ...(note.trim().length === 0 ? {} : { note: note.trim() }),
      };
      await onRecordProgress({ ...payload, commandId: commandIdFor(payload) });
      setAmount('');
      setNote('');
      setHasUnknownOutcome(false);
    } catch (cause) {
      setHasUnknownOutcome(getFinancialErrorCode(cause) === 'leader-changed');
      setError(true);
    } finally {
      setSaving(false);
    }
  }

  async function saveCycleProtection() {
    const parsedAmount =
      cycleAmount.trim() === '' ? 0 : parseMoneyInput(cycleAmount);
    if (parsedAmount === null || parsedAmount < 0) {
      setError(true);
      return;
    }
    setSaving(true);
    setError(false);
    try {
      await onSetContribution(summary.goal.id, parsedAmount);
    } catch {
      setError(true);
    } finally {
      setSaving(false);
    }
  }

  async function removeProgressEntry(entryId: string) {
    setSaving(true);
    setError(false);
    try {
      await onDeleteProgressEntry(summary.goal.id, entryId);
    } catch {
      setError(true);
    } finally {
      setSaving(false);
    }
  }

  return (
    <View
      style={{
        padding: '12px 0',
        gap: 8,
        borderBottom: `1px solid ${theme.tableBorder}`,
      }}
    >
      <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
        <View>
          <strong>{summary.goal.name}</strong>
          <span style={{ color: theme.pageTextSubdued, fontSize: 12 }}>
            {summary.goal.kind === 'emergency-fund' ? (
              <Trans>Emergency fund</Trans>
            ) : (
              <Trans>Custom target</Trans>
            )}
          </span>
        </View>
        <FundAmount
          label={<Trans>Target amount</Trans>}
          amount={summary.targetAmount}
        />
      </View>

      <progress
        aria-label={t('Goal completion')}
        value={summary.completionPercentage}
        max={100}
        style={{ width: '100%' }}
      />
      <View
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
          gap: 8,
        }}
      >
        <FundAmount
          label={<Trans>Accumulated progress</Trans>}
          amount={summary.progressAmount}
        />
        <View style={{ gap: 2 }}>
          <span style={{ color: theme.pageTextSubdued, fontSize: 11 }}>
            <Trans>Completed</Trans>
          </span>
          <strong>{summary.completionPercentage}%</strong>
        </View>
        <FundAmount
          label={<Trans>Remaining to target</Trans>}
          amount={summary.remainingAmount}
        />
      </View>

      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
        <Button
          variant={kind === 'contribution' ? 'primary' : 'normal'}
          isDisabled={isSaving || hasUnknownOutcome}
          onPress={() => setKind('contribution')}
        >
          <Trans>Add contribution</Trans>
        </Button>
        <Button
          variant={kind === 'withdrawal' ? 'primary' : 'normal'}
          isDisabled={
            isSaving ||
            hasUnknownOutcome ||
            summary.progressAmount.minorUnits === 0
          }
          onPress={() => setKind('withdrawal')}
        >
          <Trans>Withdraw / correct</Trans>
        </Button>
      </View>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
        <Input
          aria-label={t('Amount')}
          placeholder={t('Amount')}
          inputMode="decimal"
          value={amount}
          disabled={isSaving || hasUnknownOutcome}
          onChange={event => setAmount(event.target.value)}
          style={{ minWidth: 100, flex: 1 }}
        />
        <Input
          aria-label={t('Date')}
          type="date"
          value={date}
          disabled={isSaving || hasUnknownOutcome}
          onChange={event => setDate(event.target.value)}
          style={{ minWidth: 135 }}
        />
        <Input
          aria-label={t('Optional note')}
          placeholder={t('Optional note')}
          value={note}
          disabled={isSaving || hasUnknownOutcome}
          onChange={event => setNote(event.target.value)}
          style={{ minWidth: 140, flex: 2 }}
        />
        <Button
          variant="primary"
          isDisabled={isSaving}
          onPress={() => void saveProgress()}
        >
          {hasUnknownOutcome ? (
            <Trans>Retry same request</Trans>
          ) : kind === 'contribution' ? (
            <Trans>Save contribution</Trans>
          ) : (
            <Trans>Save withdrawal</Trans>
          )}
        </Button>
      </View>

      <View style={{ gap: 4 }}>
        <span style={{ color: theme.pageTextSubdued, fontSize: 12 }}>
          <Trans>
            Cycle protection is temporary and does not increase real progress.
          </Trans>
        </span>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
          <Input
            aria-label={t('Cycle protection')}
            placeholder={t('Cycle protection')}
            inputMode="decimal"
            value={cycleAmount}
            disabled={!isCurrentCycle || isSaving}
            onChange={event => setCycleAmount(event.target.value)}
            style={{ minWidth: 100 }}
          />
          <Button
            isDisabled={!isCurrentCycle || isSaving}
            onPress={() => void saveCycleProtection()}
          >
            <Trans>Save cycle protection</Trans>
          </Button>
          {summary.goal.kind === 'emergency-fund' && (
            <Button
              isDisabled={isSaving || summary.progressAmount.minorUnits === 0}
              onPress={() =>
                onUseEmergencyFund(
                  summary.goal.id,
                  summary.progressAmount.minorUnits,
                )
              }
            >
              <Trans>Use for an emergency</Trans>
            </Button>
          )}
        </View>
      </View>

      <details>
        <summary>
          <Trans>Contribution history</Trans> ({summary.history.length})
        </summary>
        {summary.history.length === 0 ? (
          <EmptySection>
            <Trans>No contributions recorded yet.</Trans>
          </EmptySection>
        ) : (
          <View style={{ gap: 4, marginTop: 6 }}>
            {summary.history.map(entry => (
              <View
                key={entry.id}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                }}
              >
                <span style={{ fontSize: 12 }}>
                  {formatProductDate(entry.date)} ·{' '}
                  {entry.kind === 'contribution' ? '+' : '-'}
                  {formatProductMoney(entry.amount)}
                  {entry.note === undefined ? '' : ` · ${entry.note}`}
                  {entry.incomeFactId !== undefined && (
                    <>
                      {' '}
                      · <Trans>From an income distribution</Trans>
                    </>
                  )}
                  {entry.expenseFactId !== undefined && (
                    <>
                      {' '}
                      ·{' '}
                      <Trans>
                        Correct this withdrawal from the emergency expense.
                      </Trans>
                    </>
                  )}
                </span>
                <Button
                  isDisabled={
                    isSaving ||
                    hasUnknownOutcome ||
                    entry.expenseFactId !== undefined
                  }
                  onPress={() => void removeProgressEntry(entry.id)}
                >
                  <Trans>Remove entry</Trans>
                </Button>
              </View>
            ))}
          </View>
        )}
      </details>

      {hasUnknownOutcome && (
        <span role="alert">
          <Trans>
            The contribution result is unknown. Retry the same request or
            refresh the budget to review its history.
          </Trans>
        </span>
      )}
      {error && !hasUnknownOutcome && (
        <span role="alert" style={{ color: theme.errorText, fontSize: 12 }}>
          <Trans>
            Enter an amount within the available goal progress and try again.
          </Trans>
        </span>
      )}
    </View>
  );
}

function FundAmount({
  label,
  amount,
}: {
  label: ReactNode;
  amount: ReadyFinancialDashboard['currentCash'];
}) {
  return (
    <View style={{ gap: 2 }}>
      <span style={{ color: theme.pageTextSubdued, fontSize: 11 }}>
        {label}
      </span>
      <FinancialText as="strong">{formatProductMoney(amount)}</FinancialText>
    </View>
  );
}
